export type TransportErrorCode =
  | "TIMEOUT"
  | "NETWORK_ERROR"
  | "HTTP_ERROR"
  | "REQUEST_ABORTED"
  | "UNKNOWN";

export interface TransportErrorOptions {
  status?: number;
  code?: TransportErrorCode;
  retryable?: boolean;
  requestId?: string;
  cause?: unknown;
}

export class TransportError extends Error {
  public readonly status?: number;
  public readonly code: TransportErrorCode;
  public readonly retryable: boolean;
  public readonly requestId?: string;
  public readonly cause?: unknown;

  constructor(message: string, options: TransportErrorOptions = {}) {
    super(message);
    this.name = "TransportError";
    this.status = options.status;
    this.code = options.code ?? "UNKNOWN";
    this.retryable = options.retryable ?? false;
    this.requestId = options.requestId;
    this.cause = options.cause;
  }
}

export function isTransportError(error: unknown): error is TransportError {
  return error instanceof TransportError;
}

export function isAbortError(error: unknown): boolean {
  return (
    error instanceof DOMException
      ? error.name === "AbortError"
      : error instanceof Error && error.name === "AbortError"
  );
}

export function isRetryableNetworkError(error: unknown): boolean {
  if (isTransportError(error)) {
    return error.retryable;
  }

  if (isAbortError(error)) {
    return true;
  }

  if (error instanceof TypeError) {
    return true;
  }

  return false;
}

export function buildHttpTransportError(
  message: string,
  status: number,
  requestId?: string
): TransportError {
  return new TransportError(message, {
    status,
    code: "HTTP_ERROR",
    retryable: false,
    requestId,
  });
}
